import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  Suspense,
  lazy,
  memo,
  useTransition,
} from "react";
import { Grid } from "react-window";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchLoanDetails } from "../../store/slices/loanDetailsSlice";
import {
  FaUserTie,
  FaBriefcase,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowRight,
  FaShieldAlt,
  FaFileInvoice,
  FaChartLine,
  FaHandHoldingUsd,
  FaUniversity,
  FaPercent,
  FaWallet,
  FaHome,
  FaCar,
  FaGraduationCap,
  FaHeartbeat,
  FaPlane,
  FaGem,
  FaRegBuilding,
  FaLandmark,
  FaPiggyBank,
  FaCreditCard,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import "./services-pages.css";
import TestimonialsSection from "../../common/testimonials-section";
import DOMPurify from "dompurify";

// ==================== STATIC ASSETS ====================
const FLOATING_ELEMENTS = Array.from({ length: 20 }).map((_, i) => ({
  id: i,
  left: `${(i * 13) % 100}%`,
  top: `${(i * 7) % 100}%`,
  size: 10 + (i % 20),
  delay: i * 0.2,
  duration: 5 + (i % 5),
  opacity: 0.1 + (i % 10) / 50,
}));

const LOAN_ICON_MAP = {
  home_loan: <FaHome />,
  car_loan: <FaCar />,
  education_loan: <FaGraduationCap />,
  personal_loan: <FaUserTie />,
  business_loan: <FaBriefcase />,
  medical_loan: <FaHeartbeat />,
  travel_loan: <FaPlane />,
  gold_loan: <FaGem />,
  property_loan: <FaRegBuilding />,
  mortgage_loan: <FaLandmark />,
  credit_card: <FaCreditCard />,
};

// ==================== ERROR BOUNDARY ====================
class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Lazy component error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="lms-error-fallback">
          Something went wrong loading this section.
        </div>
      );
    }
    return this.props.children;
  }
}

// ==================== MEMOIZED SUB‑COMPONENTS ====================
const BankCard = memo(({ bank, storageUrl }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.div
      className="lms-bank-card"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -10 }}
    >
      <motion.div
        className="lms-bank-logo-wrapper"
        animate={isHovered ? { rotate: [0, 5, -5, 0] } : {}}
        transition={{ duration: 0.5 }}
      >
        <img
          src={
            bank.bank_logo
              ? `${storageUrl || ""}/storage/${bank.bank_logo}`
              : "/images/default-bank.png"
          }
          alt={bank.name || "Bank"}
          className="lms-bank-logo"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/images/default-bank.png";
          }}
        />
      </motion.div>
    </motion.div>
  );
});

const VirtualizedBankGrid = memo(
  ({
    banks,
    columnCount,
    rowHeight,
    columnWidth,
    height,
    width,
    storageUrl,
  }) => {
    const rowCount = Math.ceil(banks.length / columnCount);
    const Cell = useCallback(
      ({ columnIndex, rowIndex, style }) => {
        const index = rowIndex * columnCount + columnIndex;
        const bank = banks[index];
        if (!bank) return null;
        return (
          <div style={style}>
            <BankCard bank={bank} storageUrl={storageUrl} />
          </div>
        );
      },
      [banks, columnCount, storageUrl],
    );
    return (
      <Grid
        columnCount={columnCount}
        columnWidth={columnWidth}
        height={height}
        rowCount={rowCount}
        rowHeight={rowHeight}
        width={width}
      >
        {Cell}
      </Grid>
    );
  },
);

const MemoizedNavBar = memo(({ tabs, activeTab, onTabClick }) => (
  <nav className="lms-sticky-navbar">
    <div className="lms-navbar-inner">
      <ul className="lms-nav-list">
        {tabs.map((tab) => (
          <motion.li
            key={tab.label}
            className={`lms-nav-item ${activeTab === tab.label ? "is-active" : ""}`}
            onClick={() => onTabClick(tab.label, tab.key)}
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
          >
            <span className="lms-nav-icon">{tab.icon}</span>
            <span className="lms-nav-label">{tab.label}</span>
            {activeTab === tab.label && (
              <motion.div
                className="lms-nav-active-indicator"
                layoutId="activeTab"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
            )}
          </motion.li>
        ))}
      </ul>
    </div>
  </nav>
));

// Lazy‑loaded heavy components
const Calculator = lazy(() => import("../../common/calculator"));
const LoanApplicationForm = lazy(() => import("./LoanApplicationForm"));

// ==================== MAIN COMPONENT ====================
const ServicesPages = () => {
  const { slug } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const STORAGE_URL = process.env.REACT_APP_URL;
  const { loanData, loading } = useSelector((state) => state.loanDetails);
  const [, startTransition] = useTransition();

  // Refs
  const heroRef = useRef(null);
  const sectionRefs = useRef({});
  const activeTabRef = useRef("");
  const observerRef = useRef(null);
  const progressBarRef = useRef(null);
  const tabMenuRef = useRef(null);
  const scrollToApplyTimeoutRef = useRef(null);

  // Local state
  const [activeTab, setActiveTab] = useState("");
  const [visibleBanksCount, setVisibleBanksCount] = useState(18);
  const [activeDocTab, setActiveDocTab] = useState("salaried");

  // Reset document tab when loan changes
  useEffect(() => {
    if (loanData) setActiveDocTab("salaried");
  }, [loanData]);

  // Tab menu definition
  const tabMenu = useMemo(
    () => [
      { label: "Apply Now", key: "apply", icon: <FaHandHoldingUsd /> },
      { label: "Features", key: "features", icon: <FaChartLine /> },
      {
        label: "Eligibility",
        key: "eligibility_criteria",
        icon: <FaCheckCircle />,
      },
      { label: "Documents", key: "documents", icon: <FaFileInvoice /> },
      { label: "Fees & Charges", key: "fees_charges", icon: <FaWallet /> },
      { label: "Policy", key: "loan_policy", icon: <FaShieldAlt /> },
      { label: "EMI Calculator", key: "emi_calculator", icon: <FaPercent /> },
      {
        label: "Partner Banks",
        key: "available_banks",
        icon: <FaUniversity />,
      },
    ],
    [],
  );
  tabMenuRef.current = tabMenu;

  const visibleTabs = useMemo(
    () =>
      tabMenu.filter(
        (tab) =>
          tab.key === "emi_calculator" ||
          tab.key === "apply" ||
          (tab.key === "available_banks" && loanData?.banks?.length > 0) ||
          (loanData && loanData[tab.key] && loanData[tab.key] !== ""),
      ),
    [tabMenu, loanData],
  );

  const getLoanIcon = useMemo(
    () => LOAN_ICON_MAP[loanData?.sub_service_slug] || <FaPiggyBank />,
    [loanData?.sub_service_slug],
  );

  // ==================== ZERO‑RENDER SCROLL PROGRESS ====================
  useEffect(() => {
    const handleScroll = () => {
      if (!progressBarRef.current) return;
      const totalScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalScroll) * 100;
      progressBarRef.current.style.width = `${progress}%`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ==================== FETCH LOAN DETAILS ====================
  useEffect(() => {
    dispatch(fetchLoanDetails({ slug }));
  }, [dispatch, slug]);

  // ==================== SCROLL TO APPLY TAB ====================
  useEffect(() => {
    if (!loading && loanData && location.state?.scrollToApply) {
      if (scrollToApplyTimeoutRef.current)
        clearTimeout(scrollToApplyTimeoutRef.current);
      scrollToApplyTimeoutRef.current = setTimeout(() => {
        handleTabClick("Apply Now", "apply");
      }, 800);
    }
    return () => {
      if (scrollToApplyTimeoutRef.current)
        clearTimeout(scrollToApplyTimeoutRef.current);
    };
  }, [loading, loanData, location.state]);

  // ==================== INTERSECTION OBSERVER (no infinite loop) ====================
  useEffect(() => {
    if (loading || !loanData) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.getAttribute("id");
          const tab = tabMenuRef.current?.find((t) => t.key === id);
          if (!tab) return;
          // Prevent duplicate updates
          if (activeTabRef.current === tab.label) return;
          activeTabRef.current = tab.label;
          startTransition(() => setActiveTab(tab.label));
        });
      },
      { root: null, rootMargin: "-150px 0px -60% 0px", threshold: 0.1 },
    );

    const elements = Object.values(sectionRefs.current);
    elements.forEach((el) => el && observerRef.current.observe(el));

    return () => observerRef.current?.disconnect();
  }, [loading, loanData, startTransition]);

  // ==================== HANDLE TAB CLICK ====================
  const handleTabClick = useCallback(
    (label, key) => {
      if (activeTabRef.current !== label) {
        activeTabRef.current = label;
        startTransition(() => setActiveTab(label));
      }
      const el = sectionRefs.current[key];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [startTransition],
  );

  // ==================== LOAD MORE BANKS ====================
  const loadMoreBanks = useCallback(() => {
    setVisibleBanksCount((prev) => prev + 18);
  }, []);

  // ==================== LOADING & ERROR STATES ====================
  if (loading) {
    return (
      <div className="lms-loader-container">
        <motion.div
          className="lms-loader"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <div className="lms-loader-inner">{getLoanIcon}</div>
        </motion.div>
        <motion.p
          className="lms-loader-text"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          Loading {loanData?.sub_service_name || "Loan"} Details...
        </motion.p>
      </div>
    );
  }

  if (!loanData) {
    return (
      <motion.div
        className="lms-error-state"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <FaTimesCircle size={60} color="#ef476f" />
        <h2>Service Not Found</h2>
        <p>The loan service you're looking for doesn't exist.</p>
        <button onClick={() => navigate("/")} className="apply-btn">
          Go Home
        </button>
      </motion.div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="lms-page-wrapper">
      <div
        ref={progressBarRef}
        className="lms-progress-bar"
        style={{ width: "0%" }}
      />

      {/* HERO SECTION */}
      <section className="lms-hero-wrapper" ref={heroRef}>
        <div className="lms-hero-background">
          <img
            src={
              process.env.PUBLIC_URL +
              `/images/services/service-${loanData?.sub_service_slug || "home_loan"}.jpg`
            }
            alt={loanData?.sub_service_name}
            className="lms-hero-bg-img"
            loading="eager"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                process.env.PUBLIC_URL +
                "/images/services/service-home_loan.jpg";
            }}
          />
        </div>
        <div className="lms-hero-overlay" />
        <div className="lms-floating-elements">
          {FLOATING_ELEMENTS.map((el) => (
            <motion.div
              key={el.id}
              className="lms-floating-element"
              animate={{ y: [0, -30, 0], x: [0, 15, 0], rotate: [0, 10, 0] }}
              transition={{
                duration: el.duration,
                repeat: Infinity,
                delay: el.delay,
              }}
              style={{
                left: el.left,
                top: el.top,
                width: el.size,
                height: el.size,
                background: `rgba(255, 255, 255, ${el.opacity})`,
              }}
            />
          ))}
        </div>
        <div className="lms-main-grid">
          <motion.div
            className="lms-content-box"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="lms-primary-title">
              {loanData?.sub_service_name || "Loan Service"}
            </h1>
            <p className="lms-sub-description">
              {loanData?.description?.replace(/<[^>]*>?/gm, "") ||
                "Get the best loan offers with minimal documentation and quick approval."}
            </p>
            <motion.button
              className="apply-btn"
              onClick={() => handleTabClick("Apply Now", "apply")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Apply Now <FaArrowRight className="lms-btn-icon" />
            </motion.button>
          </motion.div>
        </div>
        <div className="lms-wave-divider">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V0C49.49,24.3,105.77,48.57,172,56.44Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>

      {/* STICKY NAVBAR */}
      <MemoizedNavBar
        tabs={visibleTabs}
        activeTab={activeTab}
        onTabClick={handleTabClick}
      />

      {/* MAIN CONTENT */}
      <main className="lms-main-content">
        <div className="lms-content-container">
          <div className="lms-sections-wrapper">
            {visibleTabs.map((tab, index) => (
              <motion.div
                key={tab.key}
                className="lms-content-card"
                id={tab.key}
                ref={(el) => (sectionRefs.current[tab.key] = el)}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="lms-card-header">
                  <div className="lms-card-icon">{tab.icon}</div>
                  <h2 className="lms-card-heading">
                    {loanData.sub_service_name} {tab.label}
                  </h2>
                </div>
                <div className="lms-tab-body">
                  {tab.key === "emi_calculator" && (
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="lms-suspense-fallback">
                            Loading Calculator...
                          </div>
                        }
                      >
                        <Calculator
                          minRate={parseFloat(loanData?.interest_rate_min) || 8}
                          maxRate={
                            parseFloat(loanData?.interest_rate_max) || 20
                          }
                          minTenure={parseInt(loanData?.tenure_min, 10) || 1}
                          maxTenure={parseInt(loanData?.tenure_max, 10) || 30}
                          tenureUnit={loanData?.tenure_unit || "years"}
                          minAmount={
                            parseInt(loanData?.loan_amount_min, 10) || 100000
                          }
                          maxAmount={
                            parseInt(loanData?.loan_amount_max, 10) || 50000000
                          }
                        />
                      </Suspense>
                    </ErrorBoundary>
                  )}
                  {tab.key === "apply" && (
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="lms-suspense-fallback">
                            Loading Application Form...
                          </div>
                        }
                      >
                        <LoanApplicationForm loanData={loanData} />
                      </Suspense>
                    </ErrorBoundary>
                  )}
                  {tab.key === "available_banks" &&
                    loanData?.banks?.length > 0 && (
                      <div className="lms-banks-section">
                        <div className="lms-banks-grid">
                          {loanData.banks.length > 50 ? (
                            <VirtualizedBankGrid
                              banks={loanData.banks.slice(0, visibleBanksCount)}
                              columnCount={4}
                              rowHeight={200}
                              columnWidth={200}
                              height={600}
                              width={800}
                              storageUrl={STORAGE_URL}
                            />
                          ) : (
                            loanData.banks
                              .slice(0, visibleBanksCount)
                              .map((bank, idx) => (
                                <BankCard
                                  key={bank.id || `bank-${idx}`}
                                  bank={bank}
                                  storageUrl={STORAGE_URL}
                                />
                              ))
                          )}
                        </div>
                        {loanData.banks.length > visibleBanksCount && (
                          <motion.div className="lms-load-more-container">
                            <motion.button
                              className="apply-btn"
                              onClick={loadMoreBanks}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              Load More Banks
                            </motion.button>
                          </motion.div>
                        )}
                      </div>
                    )}
                  {tab.key === "documents" && (
                    <DocumentSection
                      loanData={loanData}
                      activeDocTab={activeDocTab}
                      setActiveDocTab={setActiveDocTab}
                    />
                  )}
                  {![
                    "emi_calculator",
                    "apply",
                    "available_banks",
                    "documents",
                  ].includes(tab.key) && (
                    <motion.div
                      className="lms-html-render"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(loanData[tab.key]),
                      }}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                    />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>

      <TestimonialsSection />
    </div>
  );
};

// ==================== DOCUMENTS SECTION ====================
const DocumentSection = memo(({ loanData, activeDocTab, setActiveDocTab }) => {
  const employmentOptions = useMemo(
    () => [
      {
        id: "salaried",
        icon: <FaUserTie />,
        title: "Salaried",
        desc: "Monthly salary with bank statements",
      },
      {
        id: "selfEmployed",
        icon: <FaBriefcase />,
        title: "Self Employed",
        desc: "Professional with IT returns",
      },
      {
        id: "business",
        icon: <FaBuilding />,
        title: "Other",
        desc: "Other with GST registration",
      },
    ],
    [],
  );

  return (
    <div className="lms-docs-container">
      <div className="lms-docs-header">
        <div className="lms-docs-header-icon">
          <FaFileInvoice />
        </div>
        <div className="lms-docs-header-text">
          <h3>Required Documents for {loanData?.sub_service_name || "Loan"}</h3>
          <p>Select your employment type to view the required documents</p>
        </div>
      </div>
      <div className="lms-docs-employment-options">
        {employmentOptions.map((emp) => (
          <motion.div
            key={emp.id}
            className={`lms-employment-box ${activeDocTab === emp.id ? "selected" : ""}`}
            onClick={() => setActiveDocTab(emp.id)}
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="lms-employment-icon-wrapper">{emp.icon}</div>
            <h4>{emp.title}</h4>
            <p className="lms-employment-desc">{emp.desc}</p>
            <div
              className={`lms-radio-button ${activeDocTab === emp.id ? "checked" : ""}`}
            >
              {activeDocTab === emp.id && (
                <motion.div
                  className="lms-radio-inner"
                  layoutId="activeDocTab"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </div>
          </motion.div>
        ))}
      </div>
      <div className="lms-content-area">
        <AnimatePresence mode="wait">
          {activeDocTab === "salaried" && (
            <motion.div
              key="salaried"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="lms-docs-tab-pane"
            >
              {loanData.salaried_documents ? (
                <div
                  className="lms-docs-api-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(loanData.salaried_documents),
                  }}
                />
              ) : (
                <DefaultSalariedDocs />
              )}
            </motion.div>
          )}
          {activeDocTab === "selfEmployed" && (
            <motion.div
              key="selfEmployed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="lms-docs-tab-pane"
            >
              {loanData.self_employed_documents ? (
                <div
                  className="lms-docs-api-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      loanData.self_employed_documents,
                    ),
                  }}
                />
              ) : (
                <DefaultSelfEmployedDocs />
              )}
            </motion.div>
          )}
          {activeDocTab === "business" && (
            <motion.div
              key="business"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="lms-docs-tab-pane"
            >
              {loanData.business_documents || loanData.nri_documents ? (
                <div
                  className="lms-docs-api-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      loanData.business_documents || loanData.nri_documents,
                    ),
                  }}
                />
              ) : (
                <DefaultOtherDocs />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

// Default document components (static, no re‑renders)
const DefaultSalariedDocs = () => (
  <div className="lms-docs-default-content">
    <div className="lms-default-icon">
      <FaFileInvoice />
    </div>
    <h4>Income Proof Documents for Salaried Individuals</h4>
    <div className="lms-salaried-grid">
      <div className="lms-doc-section">
        <div className="lms-doc-section-title">
          <span className="lms-section-icon">💰</span>
          <h5>Income Documents</h5>
        </div>
        <div className="lms-document-card">
          <ul>
            <li>Form 16</li>
            <li>3 Months Salary Slip</li>
            <li>6 Month Bank Account Statement</li>
            <li>Salary Certificate from Employer</li>
            <li>Latest Salary Revision Letter</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const DefaultSelfEmployedDocs = () => (
  <div className="lms-docs-default-content">
    <div className="lms-docs-default-icon">
      <FaFileInvoice />
    </div>
    <h4>Documents Required for Self-Employed Individuals</h4>
    <div className="lms-docs-document-grid">
      <div className="lms-docs-document-card">
        <h5>Registration Documents</h5>
        <ul>
          <li>PAN</li>
          <li>Trade License</li>
          <li>Partnership Deed</li>
          <li>Article of Association</li>
          <li>Memorandum of Association</li>
          <li>Import Export Code</li>
          <li>SEBI Registration Certificate</li>
          <li>ROC Registration Certificate</li>
        </ul>
      </div>
      <div className="lms-docs-document-card">
        <h5>Financial Documents</h5>
        <ul>
          <li>Financial Statement Audited by CA</li>
          <li>Profit & Loss Account Statement</li>
          <li>Balance Sheet</li>
          <li>6 Months Bank Account Statement</li>
        </ul>
      </div>
      <div className="lms-docs-document-card">
        <h5>Other Documents</h5>
        <ul>
          <li>Professional Practice License</li>
          <li>Registration Certificate of Establishment</li>
          <li>Business Address Proof</li>
        </ul>
      </div>
    </div>
  </div>
);

const DefaultOtherDocs = () => (
  <div className="lms-docs-default-content">
    <div className="lms-docs-default-icon">
      <FaFileInvoice />
    </div>
    <h4>Documents Required for Other/NRI Applicants</h4>
    <div className="lms-docs-document-grid">
      <div className="lms-docs-document-card">
        <h5>Income Proof Documents</h5>
        <ul>
          <li>Income Proof Documents for NRI</li>
        </ul>
      </div>
      <div className="lms-docs-document-card">
        <h5>Property Documents</h5>
        <ul>
          <li>Property Papers</li>
          <li>Sale Deed</li>
          <li>Stamped Agreement of Sale</li>
          <li>Allotment Letter</li>
        </ul>
      </div>
      <div className="lms-docs-document-card">
        <h5>NOC & Certificates</h5>
        <ul>
          <li>No Objection Certificate (NOC)</li>
          <li>Housing Society NOC</li>
          <li>Builder NOC</li>
          <li>Possession Certificate</li>
          <li>Occupancy Certificate</li>
        </ul>
      </div>
      <div className="lms-docs-document-card">
        <h5>Payment & Tax Documents</h5>
        <ul>
          <li>Land Tax Receipt</li>
          <li>Construction Cost Estimate</li>
          <li>Bank Account Statement of Payment</li>
          <li>Payment Receipt</li>
          <li>Share Certificate (for resale property)</li>
        </ul>
      </div>
    </div>
  </div>
);

export default ServicesPages;
